import type { Dayjs } from 'dayjs';
import type {
  ClosingPackageApprovalResult,
  RegisterClientApprovalBody,
} from '../types';
import {
  validateTaskModalEventDate,
  validateTaskModalRequiredComment,
  validateTaskModalRequiredText,
} from './taskModalValidation';
import { isCommentRequired, trimOptionalComment } from './commentPolicy';
import { CLOSING_PACKAGE_TASK_TYPE } from '../workspace/closingPackageTaskTypes';

export type ClientApprovalSource = RegisterClientApprovalBody['source'];

export type ClientApprovalValidationCode =
  | 'EVENT_DATE_REQUIRED'
  | 'RESULT_REQUIRED'
  | 'SOURCE_REQUIRED'
  | 'COMMENT_REQUIRED'
  | 'REJECTION_REASON_REQUIRED';

export type ClientApprovalValidationIssue = {
  code: ClientApprovalValidationCode;
  message: string;
};

export type ValidateClientApprovalFormInput = {
  eventAt: Dayjs | null | undefined;
  result: ClosingPackageApprovalResult | null | undefined;
  source: ClientApprovalSource | null | undefined;
  rejectionReason: string | null | undefined;
  comment: string | null | undefined;
};

export const CLIENT_APPROVAL_SOURCE_OPTIONS: Array<{
  value: ClientApprovalSource;
  label: string;
}> = [
  { value: 'EMAIL', label: 'E-mail' },
  { value: 'PHONE', label: 'Телефон' },
  { value: 'EDO', label: 'EDI' },
  { value: 'MEETING', label: 'Лично' },
  { value: 'OTHER', label: 'Другое' },
];

export const CLIENT_APPROVAL_RESULT_OPTIONS: Array<{
  value: ClosingPackageApprovalResult;
  label: string;
}> = [
  { value: 'APPROVED', label: 'Комплект согласован' },
  { value: 'REJECTED', label: 'Требуется доработка' },
];

export const CLIENT_APPROVAL_NEXT_TASK = {
  APPROVED: CLOSING_PACKAGE_TASK_TYPE.PREPARE_ORIGINALS,
  REJECTED: CLOSING_PACKAGE_TASK_TYPE.REVISE_CLOSING_PACKAGE,
} as const;

export function resolveClientApprovalNextTask(
  result: ClosingPackageApprovalResult
): (typeof CLIENT_APPROVAL_NEXT_TASK)[keyof typeof CLIENT_APPROVAL_NEXT_TASK] {
  return result === 'REJECTED'
    ? CLIENT_APPROVAL_NEXT_TASK.REJECTED
    : CLIENT_APPROVAL_NEXT_TASK.APPROVED;
}

export function buildClientApprovalComment(input: {
  result: ClosingPackageApprovalResult;
  rejectionReason?: string | null;
  comment: string;
}): string {
  const trimmedComment = input.comment.trim();
  if (input.result !== 'REJECTED') {
    return trimmedComment;
  }

  const trimmedReason = String(input.rejectionReason ?? '').trim();
  if (!trimmedReason) {
    return trimmedComment;
  }

  return `Причина доработки: ${trimmedReason}\n${trimmedComment}`;
}

export function validateClientApprovalFormFields(
  input: ValidateClientApprovalFormInput
): ClientApprovalValidationIssue | null {
  const eventIssue = validateTaskModalEventDate(
    input.eventAt,
    'Укажите дату ответа клиента'
  );
  if (eventIssue) {
    return eventIssue as ClientApprovalValidationIssue;
  }

  if (!input.result) {
    return {
      code: 'RESULT_REQUIRED',
      message: 'Выберите результат согласования',
    };
  }

  if (!input.source) {
    return {
      code: 'SOURCE_REQUIRED',
      message: 'Укажите способ получения ответа',
    };
  }

  if (input.result === 'REJECTED') {
    const commentIssue = validateTaskModalRequiredComment(input.comment, 'Укажите комментарий');
    if (commentIssue) {
      return commentIssue as ClientApprovalValidationIssue;
    }

    const reasonIssue = validateTaskModalRequiredText(
      input.rejectionReason,
      'REJECTION_REASON_REQUIRED',
      'Укажите причину доработки'
    );
    if (reasonIssue) {
      return reasonIssue as ClientApprovalValidationIssue;
    }
  }

  return null;
}

export function validateClientApprovalSubmit(
  input: ValidateClientApprovalFormInput
): ClientApprovalValidationIssue | null {
  return validateClientApprovalFormFields(input);
}

export function buildClientApprovalBody(input: {
  result: ClosingPackageApprovalResult;
  source: ClientApprovalSource;
  rejectionReason?: string | null;
  comment: string | null | undefined;
  registeredAt: string;
}): RegisterClientApprovalBody {
  const body: RegisterClientApprovalBody = {
    result: input.result,
    source: input.source,
    registeredAt: input.registeredAt,
  };
  const builtComment = buildClientApprovalComment({
    result: input.result,
    rejectionReason: input.rejectionReason,
    comment: String(input.comment ?? ''),
  });
  const trimmedComment = trimOptionalComment(builtComment);
  if (trimmedComment) {
    body.comment = trimmedComment;
  }
  return body;
}

export function isClientApprovalCommentRequired(
  result: ClosingPackageApprovalResult | null | undefined
): boolean {
  return isCommentRequired(result);
}

export function getClientApprovalSuccessMessage(result: ClosingPackageApprovalResult): string {
  return result === 'REJECTED'
    ? 'Ответ клиента зарегистрирован: требуется доработка'
    : 'Ответ клиента зарегистрирован: комплект согласован';
}
